/**
 * @sazeo/sdk — Helpdesk hooks
 *
 * TanStack Query wrappers for the helpdesk addon. All calls route through
 * the Edge Function at `/addons/helpdesk/*` guarded by
 * requireAddonEnabled() and enforced by RLS via the caller's JWT.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClientLike } from './people';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface HelpdeskTicket {
  id: string;
  tenant_id: string;
  ticket_no: number;
  subject: string;
  description: string | null;
  category: 'it' | 'hr' | 'facilities' | 'finance' | 'general' | 'other';
  status: 'open' | 'in_progress' | 'waiting' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  submitted_by: string;
  assigned_to: string | null;
  resolved_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface HelpdeskReply {
  id: string;
  tenant_id: string;
  ticket_id: string;
  author_id: string;
  body: string;
  is_internal: boolean;
  created_at: string;
}

export interface HelpdeskTicketCreateInput {
  subject: string;
  description?: string | null;
  category?: HelpdeskTicket['category'];
  priority?: HelpdeskTicket['priority'];
}

export interface HelpdeskTicketUpdateInput {
  status?: HelpdeskTicket['status'];
  priority?: HelpdeskTicket['priority'];
  assigned_to?: string | null;
  category?: HelpdeskTicket['category'];
}

export interface HelpdeskReplyCreateInput {
  body: string;
  is_internal?: boolean;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const helpdeskKeys = {
  all: ['helpdesk'] as const,
  list: (tenantId: string) => [...helpdeskKeys.all, 'list', tenantId] as const,
  detail: (id: string) => [...helpdeskKeys.all, 'detail', id] as const,
  replies: (tenantId: string, ticketId: string) =>
    [...helpdeskKeys.all, 'replies', tenantId, ticketId] as const,
} as const;

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

async function getAuthHeaders(supabase: SupabaseClientLike): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function apiFetch<T>(
  supabase: SupabaseClientLike,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const headers = await getAuthHeaders(supabase);
  const res = await fetch(`/functions/v1/${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...headers, ...init?.headers },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
      error?: { message?: string };
    };
    throw new Error(body.error?.message ?? res.statusText);
  }
  if (res.status === 204) return null as T;
  return res.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useHelpdeskTickets(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: helpdeskKeys.list(tenantId),
    queryFn: () => apiFetch<HelpdeskTicket[]>(supabase, 'addons/helpdesk/tickets'),
    enabled: Boolean(tenantId),
  });
}

export function useHelpdeskTicket(
  supabase: SupabaseClientLike,
  tenantId: string,
  ticketId: string,
) {
  void tenantId;
  return useQuery({
    queryKey: helpdeskKeys.detail(ticketId),
    queryFn: () => apiFetch<HelpdeskTicket>(supabase, `addons/helpdesk/tickets/${ticketId}`),
    enabled: Boolean(ticketId),
  });
}

export function useHelpdeskReplies(
  supabase: SupabaseClientLike,
  tenantId: string,
  ticketId: string,
) {
  return useQuery({
    queryKey: helpdeskKeys.replies(tenantId, ticketId),
    queryFn: () =>
      apiFetch<HelpdeskReply[]>(supabase, `addons/helpdesk/tickets/${ticketId}/replies`),
    enabled: Boolean(ticketId),
  });
}

export function useCreateHelpdeskTicket(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: HelpdeskTicketCreateInput) =>
      apiFetch<HelpdeskTicket>(supabase, 'addons/helpdesk/tickets', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: helpdeskKeys.list(tenantId) });
    },
  });
}

export function useUpdateTicket(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: HelpdeskTicketUpdateInput }) =>
      apiFetch<HelpdeskTicket>(supabase, `addons/helpdesk/tickets/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: helpdeskKeys.detail(vars.id) });
      void qc.invalidateQueries({ queryKey: helpdeskKeys.list(tenantId) });
    },
  });
}

export function useAddReply(supabase: SupabaseClientLike, tenantId: string, ticketId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: HelpdeskReplyCreateInput) =>
      apiFetch<HelpdeskReply>(supabase, `addons/helpdesk/tickets/${ticketId}/replies`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: helpdeskKeys.replies(tenantId, ticketId) });
    },
  });
}
